import { supabase, TABLES } from '../lib/supabase';
import { Race, Runner, RaceMeeting } from '../types';
import { CACHE_TTL, getCachedData, setCachedData } from './cacheService';

const getTodayKey = (): string => new Date().toISOString().split('T')[0];

export interface FromThePaddock {
  id: string;
  date: string;
  from_the_paddock_summary: string;
  created_at: string;
  updated_at: string;
}

export interface TodaysRaceData {
  meetings: RaceMeeting[];
  runners: Runner[];
}

const RACE_ID_CHUNK_SIZE = 100;

const normalizeHorseName = (name: string | null | undefined) =>
  (name ?? '').replace(/\s*\([^)]*\)\s*/g, '').trim();

// Helper function to fetch all pages from Supabase (handles 1000 row limit)
async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => any,
  pageSize: number = 1000
): Promise<T[]> {
  const allData: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const to = from + pageSize - 1;
    const { data, error } = await buildQuery(from, to);

    if (error) {
      throw error;
    }

    if (data && data.length > 0) {
      allData.push(...data);
      hasMore = data.length === pageSize;
      from += pageSize;
    } else {
      hasMore = false;
    }
  }

  return allData;
}

async function fetchRunnersChunkWithRetry(
  raceIds: string[],
  columns: string,
  attempts = 3
): Promise<Runner[] | null> {
  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const buildQuery = (from: number, to: number) =>
        supabase
          .from(TABLES.RUNNERS)
          .select(columns)
          .in('race_id', raceIds)
          .range(from, to);
      return await fetchAllPages<Runner>(buildQuery);
    } catch (err) {
      lastErr = err;
    }
    if (attempt < attempts) {
      await new Promise((r) => setTimeout(r, attempt === 1 ? 150 : 400));
    }
  }
  console.error(
    `Error fetching runners for chunk of ${raceIds.length} race(s) after ${attempts} attempts:`,
    lastErr
  );
  return null;
}

async function fetchRunnersByRaceIds(raceIds: string[]): Promise<Runner[]> {
  if (raceIds.length === 0) {
    return [];
  }

  const allRunners: Runner[] = [];
  for (let i = 0; i < raceIds.length; i += RACE_ID_CHUNK_SIZE) {
    const chunkIds = raceIds.slice(i, i + RACE_ID_CHUNK_SIZE);
    const runners = await fetchRunnersChunkWithRetry(chunkIds, '*');
    if (runners === null) {
      throw new Error(`Failed to fetch runners for chunk of ${chunkIds.length} race(s)`);
    }
    allRunners.push(...runners);
  }
  return allRunners;
}

export function buildRaceMeetings(races: Race[], runners: Runner[]): RaceMeeting[] {
  const meetingsMap = new Map<string, RaceMeeting>();

  races.forEach(race => {
    const meetingKey = `${race.course}_${race.race_date}`;

    if (!meetingsMap.has(meetingKey)) {
      meetingsMap.set(meetingKey, {
        meeting_id: `meeting_${race.course.toLowerCase().replace(/\s+/g, '_')}_${race.race_date}`,
        course: race.course,
        meeting_date: race.race_date,
        meeting_type: race.race_type,
        going: race.going,
        surface: race.surface,
        total_races: 0,
        first_race_time: race.race_time,
        last_race_time: race.race_time,
        races: []
      });
    }

    const aiSelectionRunner = race.ai_selection
      ? runners.find(runner =>
          runner.race_id === race.race_id &&
          normalizeHorseName(runner.horse_name) === normalizeHorseName(race.ai_selection)
        )
      : undefined;

    const raceWithCorrectOdds = {
      ...race,
      bet365_decimal_odds: aiSelectionRunner?.bet365_decimal_odds || race.bet365_decimal_odds
    };

    const meeting = meetingsMap.get(meetingKey)!;
    meeting.races.push(raceWithCorrectOdds);
    meeting.total_races++;

    if (race.race_time < meeting.first_race_time) {
      meeting.first_race_time = race.race_time;
    }
    if (race.race_time > meeting.last_race_time) {
      meeting.last_race_time = race.race_time;
    }
  });

  return Array.from(meetingsMap.values())
    .sort((a, b) => a.first_race_time.localeCompare(b.first_race_time));
}

const inflightTodaysRunners = new Map<string, Promise<Runner[]>>();

// Fetch all races for today
export const fetchTodaysRaces = async (): Promise<Race[]> => {
  const today = getTodayKey();
  const cacheKey = `cache_todays_races_${today}`;

  const cached = getCachedData<Race[]>(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase
      .from(TABLES.RACES)
      .select('*')
      .eq('race_date', today)
      .eq('ai_qualified', 'yes')
      .order('race_time', { ascending: true });

    if (error) {
      console.error('Error fetching races:', error);
      throw error;
    }

    const races = data || [];
    setCachedData(cacheKey, races, CACHE_TTL.TEN_MINUTES);
    return races;
  } catch (error) {
    console.error('Failed to fetch races:', error);
    throw error;
  }
};

// Fetch all runners for today's ai_qualified races
export const fetchTodaysRunners = async (): Promise<Runner[]> => {
  const today = getTodayKey();
  const cacheKey = `cache_todays_runners_v2_${today}`;

  const cached = getCachedData<Runner[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const inflight = inflightTodaysRunners.get(today);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    try {
      const races = await fetchTodaysRaces();
      const raceIds = races.map((race) => race.race_id);
      const runners = await fetchRunnersByRaceIds(raceIds);
      setCachedData(cacheKey, runners, CACHE_TTL.TEN_MINUTES);
      return runners;
    } catch (error) {
      console.error('Failed to fetch runners:', error);
      throw error;
    } finally {
      inflightTodaysRunners.delete(today);
    }
  })();

  inflightTodaysRunners.set(today, promise);
  return promise;
};

export const fetchTodaysRaceData = async (): Promise<TodaysRaceData> => {
  const today = getTodayKey();
  const cacheKey = `cache_todays_race_data_v2_${today}`;

  const cached = getCachedData<TodaysRaceData>(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const races = await fetchTodaysRaces();
    const raceIds = races.map((race) => race.race_id);
    const runners = await fetchRunnersByRaceIds(raceIds);
    const meetings = buildRaceMeetings(races, runners);
    const result = { meetings, runners };
    setCachedData(cacheKey, result, CACHE_TTL.TEN_MINUTES);
    return result;
  } catch (error) {
    console.error('Failed to fetch today\'s race data:', error);
    throw error;
  }
};

// Fetch races grouped by meeting with correct odds from runners
export const fetchRaceMeetings = async (): Promise<RaceMeeting[]> => {
  const { meetings } = await fetchTodaysRaceData();
  return meetings;
};

// Fetch runners for a specific race
export const fetchRunnersForRace = async (raceId: string): Promise<Runner[]> => {
  try {
    const { data, error } = await supabase
      .from(TABLES.RUNNERS)
      .select('*')
      .eq('race_id', raceId)
      .order('draw', { ascending: true });

    if (error) {
      console.error('Error fetching runners for race:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch runners for race:', error);
    throw error;
  }
};

// Fetch all completed races (races with results)
export const fetchCompletedRaces = async (): Promise<Race[]> => {
  const CACHE_KEY = 'cache_completed_races';
  
  // Check cache first
  const cachedData = getCachedData<Race[]>(CACHE_KEY);
  if (cachedData) {
    return cachedData;
  }

  try {
    const buildQuery = (from: number, to: number) =>
      supabase
        .from(TABLES.RACES)
        .select('*')
        .not('race_result', 'is', null)
        .order('race_date', { ascending: false })
        .order('race_time', { ascending: false })
        .range(from, to);

    const races = await fetchAllPages<Race>(buildQuery);

    const runnersByRaceId = new Map<string, Runner[]>();
    const failedRaceIds = new Set<string>();

    for (let i = 0; i < races.length; i += RACE_ID_CHUNK_SIZE) {
      const chunkRaces = races.slice(i, i + RACE_ID_CHUNK_SIZE);
      const chunkRaceIds = chunkRaces.map((r) => r.race_id);
      const runners = await fetchRunnersChunkWithRetry(
        chunkRaceIds,
        'race_id, horse_name, bet365_decimal_odds'
      );
      if (runners === null) {
        for (const id of chunkRaceIds) failedRaceIds.add(id);
        continue;
      }
      for (const runner of runners) {
        const list = runnersByRaceId.get(runner.race_id);
        if (list) {
          list.push(runner);
        } else {
          runnersByRaceId.set(runner.race_id, [runner]);
        }
      }
    }

    const racesWithCorrectOdds: Race[] = races.map((race) => {
      if (failedRaceIds.has(race.race_id)) {
        return race;
      }
      const runners = runnersByRaceId.get(race.race_id) ?? [];
      const normalizedAISelection = normalizeHorseName(race.ai_selection ?? '');
      const aiSelectionRunner = runners.find(
        (runner) => normalizeHorseName(runner.horse_name ?? '') === normalizedAISelection
      );
      return {
        ...race,
        bet365_decimal_odds:
          aiSelectionRunner?.bet365_decimal_odds || race.bet365_decimal_odds,
      };
    });

    if (failedRaceIds.size > 0) {
      console.warn(
        `[fetchCompletedRaces] ${failedRaceIds.size} race(s) had no runners fetched after retries; their odds will fall back to the race row and may be skipped from P/L by the ResultsHistory guard.`
      );
    }

    setCachedData(CACHE_KEY, racesWithCorrectOdds, CACHE_TTL.FIFTEEN_MINUTES);

    return racesWithCorrectOdds;
  } catch (error) {
    console.error('Failed to fetch completed races:', error);
    throw error;
  }
};

// Fetch from_the_paddock_summary for today's date
export const fetchTodaysPaddockSummary = async (): Promise<string | null> => {
  const today = getTodayKey();
  const cacheKey = `cache_paddock_summary_${today}`;

  const cached = getCachedData<string | null>(cacheKey);
  if (cached !== null) {
    return cached;
  }

  try {
    const { data, error } = await supabase
      .from('from_the_paddock')
      .select('from_the_paddock_summary')
      .eq('date', today)
      .maybeSingle();

    if (error) {
      console.error('Error fetching paddock summary:', error);
      return null;
    }

    const summary = data?.from_the_paddock_summary || null;
    if (summary !== null) {
      setCachedData(cacheKey, summary, CACHE_TTL.THIRTY_MINUTES);
    }
    return summary;
  } catch (error) {
    console.error('Failed to fetch paddock summary:', error);
    return null;
  }
};
